import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { db, auth } from './firebase';
import { collection, addDoc, Timestamp, deleteDoc, doc } from './firestore-compat';
import { Upload as UploadIcon } from 'lucide-react';
import { grupoDe } from './visitasGrupos';
import { listarUploads } from './visitasConsolidado';

const COLL = 'visitas_domiciliares';
const C = { primary: '#6366f1', success: '#22c55e', danger: '#ef4444' };

// Cards de especialidade aparecem mesmo sem upload ainda, pra já existir um lugar
// pra cada uma. Assim que a primeira planilha de uma especialidade nova é enviada
// (via "+ Nova especialidade"), ela passa a aparecer sozinha nas próximas visitas.
const ESPECIALIDADES_SEED = [
  'Fisioterapeuta', 'Fonoaudiólogo(a)', 'Enfermeiro(a)', 'Médico(a)',
  'Terapeuta Ocupacional', 'Nutricionista', 'Fisioterapeuta Bobath',
  'Fisioterapeuta RTA', 'Fonoaudiólogo(a) Bobath',
];

// Mesma paleta/estratégia de cor por nome do DashboardVisitas.js e do
// VisitasEspecialidadeHub.js, duplicada aqui pra cor de cada especialidade bater
// nos três lugares (arquivos autônomos, sem lib de componentes compartilhada).
const PALETTE = [
  '#0F766E', '#2563EB', '#9333EA', '#DB2777', '#D4351C', '#B5790B',
  '#15803D', '#0891B2', '#7C3AED', '#BE123C', '#4D7C0F', '#A16207',
  '#0E7490', '#9D174D', '#1D4ED8'
];
const specColorCache = {};
function specColor(name) {
  if (specColorCache[name] !== undefined) return specColorCache[name];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  const c = PALETTE[h % PALETTE.length];
  specColorCache[name] = c;
  return c;
}

function isHoje(d) {
  if (!d) return false;
  const hoje = new Date();
  return d.getFullYear() === hoje.getFullYear() && d.getMonth() === hoje.getMonth() && d.getDate() === hoje.getDate();
}

function toJSDate(v) {
  if (!v) return null;
  if (typeof v === 'string') { const d = new Date(v); return isNaN(d) ? null : d; }
  if (typeof v.toDate === 'function') return v.toDate();
  if (typeof v._seconds === 'number') return new Date(v._seconds * 1000);
  return null;
}

function parseCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) return [];
  const headers = lines[0].split(';').map(h => h.trim().replace(/^"|"$/g, ''));
  return lines.slice(1).map(line => {
    const cols = line.split(';').map(v => v.trim().replace(/^"|"$/g, ''));
    const obj = {};
    headers.forEach((h, i) => { obj[h] = cols[i] !== undefined ? cols[i] : ''; });
    return obj;
  }).filter(row => Object.values(row).some(v => v !== ''));
}

// ── Suporte ao arquivo bruto exportado do IW (TDEXP_VISITASNR.TXT) ─────────────
// Mesmo formato/mapeamento usado por api/pull-ftp-visitas.js — mantido em sincronia
// para que o upload manual aceite o arquivo exatamente como sai do FTP do IW.
const IW_VISITAS_COLS = [
  'ID_ATEND', 'PATIENTNAME', 'IDENTERPRISE', 'STATUS', 'CHECKOUTDATE',
  'AGENDASTARTDATE', 'AGENDAENDDATE', 'PROFPROVIDERNAME', 'CODENAME',
  'FREQUENCIA', 'HEALTHPROVIDERNAME',
];

// TDEXP_ATEND.txt (Censo Homecare) também começa com "ID_ATEND" — não dá pra
// diferenciar os dois exports do IW só pela primeira coluna. PATIENTNAME e
// AGENDASTARTDATE só existem no TDEXP_VISITASNR.TXT, então servem de assinatura.
function isRawIwExport(text) {
  const firstLine = (text.split(/\r?\n/)[0] || '').toUpperCase();
  return firstLine.includes('PATIENTNAME') && firstLine.includes('AGENDASTARTDATE');
}

function looksLikeOtherIwExport(text) {
  const firstLine = (text.split(/\r?\n/)[0] || '');
  return /(^|\|)\s*ID_ATEND\s*(\||$)/i.test(firstLine);
}

function mapIwVisitaRow(r) {
  const filialRaw = String(r.HEALTHPROVIDERNAME || '').trim();
  const filial = filialRaw.replace(/\s*HC\s*$/i, '').trim(); // "HomeSenior HC" → "HomeSenior"
  const idAgenda = [r.ID_ATEND, r.CODENAME, r.AGENDASTARTDATE].filter(Boolean).join('|');
  return {
    'ID Agenda':          idAgenda,
    'Paciente':           r.PATIENTNAME || '',
    'Operadora':          r.IDENTERPRISE || '',
    'Status':             r.STATUS || '',
    'Cod. Especialidade': r.CODENAME || '',
    'Frequência':         r.FREQUENCIA || '',
    'Profiss. Agendado':  r.PROFPROVIDERNAME || '',
    'Agendar de:':        r.AGENDASTARTDATE || '',
    'Agendar até:':       r.AGENDAENDDATE || '',
    'Data Agendada':      '',
    'Data Realiz.':       '',
    'Data do Registro':   '',
    'Filial':             filial,
  };
}

function parseIwPipeVisitas(text) {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rawLines = normalized.split('\n');
  if (rawLines.length === 0) return [];
  const headerLine = rawLines[0].split('|');
  const colCount = Math.min(IW_VISITAS_COLS.length, headerLine.length);

  // Junta linhas de continuação (campos com quebra de linha embutida no export do IW)
  const lines = [];
  for (let i = 1; i < rawLines.length; i++) {
    const l = rawLines[i];
    if (!l || !l.trim()) continue;
    if (/^\d/.test(l)) {
      lines.push(l);
    } else if (lines.length > 0) {
      lines[lines.length - 1] += ' ' + l.trim();
    }
  }

  const rows = lines.map(line => {
    const values = line.split('|');
    const row = {};
    IW_VISITAS_COLS.slice(0, colCount).forEach((col, idx) => { row[col] = (values[idx] || '').trim(); });
    return row;
  }).filter(r => r.ID_ATEND);

  return rows.map(mapIwVisitaRow);
}

// Detecta a especialidade predominante nas linhas (coluna "Cod. Especialidade" / "Especialidade")
// — usada só como aviso de conferência: quem manda o arquivo pro card certo é o próprio usuário.
function detectEspecialidade(rows) {
  const counts = {};
  rows.forEach(r => {
    const v = String(r['Cod. Especialidade'] || r['Especialidade'] || '').trim();
    if (v) counts[v] = (counts[v] || 0) + 1;
  });
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return sorted.length ? sorted[0][0] : '';
}

// Card de upload de uma especialidade — componente à parte (fora do componente principal)
// pra não perder o estado de "arrastando" a cada re-render da tela.
function EspCard({ esp, meta, highlighted, onFile }) {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();
  const col = specColor(esp);
  const grupo = grupoDe(esp);
  const feitoHoje = isHoje(meta?.uploadedAt);

  const borderColor = dragging ? C.primary : feitoHoje ? C.success : highlighted ? '#0F766E' : '#e2e8f0';
  const boxShadow = dragging
    ? '0 0 0 3px rgba(99,102,241,0.15)'
    : feitoHoje
      ? '0 0 0 3px rgba(34,197,94,0.15)'
      : highlighted
        ? '0 0 0 3px rgba(15,118,110,0.15)'
        : '0 1px 2px rgba(20,33,48,.04)';

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) onFile(esp, f); }}
      onClick={() => inputRef.current?.click()}
      style={{
        background: feitoHoje ? '#f0fdf4' : '#fff', borderRadius: 12, padding: '14px 16px 12px', cursor: 'pointer',
        position: 'relative', overflow: 'hidden', transition: 'all .15s',
        border: `1.5px solid ${borderColor}`,
        boxShadow,
      }}
    >
      <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: col }} />
      {feitoHoje && (
        <span style={{
          position: 'absolute', top: 10, right: 10, width: 19, height: 19, borderRadius: '50%',
          background: C.success, color: '#fff', display: 'grid', placeItems: 'center', fontSize: 11, fontWeight: 900, lineHeight: 1,
        }}>✓</span>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 9, paddingRight: feitoHoje ? 22 : 0 }}>
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: col, flexShrink: 0 }} />
        <span style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.86rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={esp}>{esp}</span>
      </div>
      {grupo !== esp && (
        <div style={{ fontSize: '0.64rem', color: '#94a3b8', marginBottom: 6, marginTop: -4 }}>↳ conta em "{grupo}" no painel</div>
      )}
      {meta ? (
        <div style={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.5 }}>
          <div style={{ color: '#334155', fontWeight: 600 }}>{meta.rowCount} linhas</div>
          <div>{meta.uploadedAt?.toLocaleString?.('pt-BR') || 'data indisponível'}</div>
          {feitoHoje && <div style={{ color: C.success, fontWeight: 700, marginTop: 2 }}>✓ Enviado hoje</div>}
        </div>
      ) : (
        <div style={{ fontSize: '0.72rem', color: '#cbd5e1', fontStyle: 'italic' }}>Nenhum upload ainda</div>
      )}
      <div style={{
        marginTop: 10, fontSize: '0.72rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 5,
        color: dragging ? C.primary : feitoHoje ? C.success : '#94a3b8',
      }}>
        <UploadIcon size={12} /> {dragging ? 'Solte aqui' : feitoHoje ? 'Substituir arquivo' : 'Enviar CSV'}
      </div>
      <input
        ref={inputRef} type="file" accept=".csv,.txt" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files[0]; if (f) onFile(esp, f); e.target.value = ''; }}
      />
    </div>
  );
}

export default function UploadVisitas() {
  const [searchParams] = useSearchParams();
  const espFromLink = searchParams.get('esp') || '';

  const [history, setHistory] = useState([]);
  const [extraEsp, setExtraEsp] = useState([]); // especialidades adicionadas na hora, sem upload ainda
  const [addingNew, setAddingNew] = useState(false);
  const [newEspName, setNewEspName] = useState('');
  const [confirm, setConfirm] = useState(null); // { esp, fileName, rows, detected }
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null);
  const [historyFilter, setHistoryFilter] = useState('');
  const currentUser = auth.currentUser;

  useEffect(() => {
    // Se veio de "Carregar CSV" a partir do painel de uma especialidade que ainda
    // não tem card (nunca foi subida), garante que o card apareça pra receber o upload.
    if (espFromLink) setExtraEsp(prev => prev.includes(espFromLink) ? prev : [...prev, espFromLink]);
    loadHistory();
  }, []); // eslint-disable-line

  const loadHistory = async () => {
    try {
      // Só a identificação de cada planilha (as linhas somam ~40 MB e travavam a lista).
      const lista = await listarUploads(80);
      setHistory(lista.map(h => ({ ...h, uploadedAt: toJSDate(h.uploadedAt) })));
    } catch { /* ignore */ }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente excluir esta planilha? Os dados serão removidos do painel.")) return;
    try {
      await deleteDoc(doc(db, COLL, id));
      setMsg({ type: 'success', text: 'Planilha excluída com sucesso!' });
      loadHistory();
    } catch (e) {
      setMsg({ type: 'error', text: `Erro ao excluir: ${e.message}` });
    }
  };

  // último upload de cada especialidade (histórico já vem ordenado por data desc)
  const latestByEsp = useMemo(() => {
    const map = {};
    history.forEach(h => {
      const key = h.especialidade || '(sem especialidade)';
      if (!map[key]) map[key] = h;
    });
    return map;
  }, [history]);

  // cards exibidos: especialidades sugeridas + tudo que já apareceu no histórico + adicionadas na hora
  const cards = useMemo(() => {
    const set = new Set(ESPECIALIDADES_SEED);
    history.forEach(h => { if (h.especialidade) set.add(h.especialidade); });
    extraEsp.forEach(e => set.add(e));
    return [...set].sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [history, extraEsp]);

  // "Atual" no histórico completo = primeiro doc de cada especialidade na lista
  const latestPerEspecialidade = useMemo(() => {
    const seen = new Set();
    const map = {};
    history.forEach(h => {
      const key = h.especialidade || '(sem especialidade)';
      if (!seen.has(key)) { seen.add(key); map[h.id] = true; }
    });
    return map;
  }, [history]);

  const especialidadesNoHistorico = useMemo(
    () => [...new Set(history.map(h => h.especialidade || '(sem especialidade)'))].sort(),
    [history]
  );

  const filteredHistory = useMemo(() => {
    if (!historyFilter) return history;
    return history.filter(h => (h.especialidade || '(sem especialidade)') === historyFilter);
  }, [history, historyFilter]);

  // Arquivo solto/selecionado num card específico — a especialidade é a do card, não a detectada.
  const handleFile = async (esp, file) => {
    if (!file || !/\.(csv|txt)$/i.test(file.name)) {
      setMsg({ type: 'error', text: 'Selecione um arquivo .csv ou .txt' });
      return;
    }
    setMsg(null);
    try {
      const buffer = await file.arrayBuffer();
      const text = new TextDecoder('iso-8859-1').decode(buffer);
      let rows;
      if (isRawIwExport(text)) {
        rows = parseIwPipeVisitas(text);
      } else if (looksLikeOtherIwExport(text)) {
        throw new Error('Este arquivo parece ser outro export do IW (não é o TDEXP_VISITASNR.TXT de Visitas Domiciliares — talvez seja o TDEXP_ATEND.txt, que é do Censo Homecare). Confira o arquivo e tente novamente.');
      } else {
        rows = parseCSV(text);
      }
      if (rows.length === 0) throw new Error('Arquivo vazio ou formato inválido');

      const detected = detectEspecialidade(rows);
      setConfirm({ esp, fileName: file.name, rows, detected });
    } catch (e) {
      setMsg({ type: 'error', text: `Erro: ${e.message}` });
    }
  };

  const handleConfirmImport = async () => {
    if (!confirm) return;
    setUploading(true);
    try {
      await addDoc(collection(db, COLL), {
        uploadedAt: Timestamp.now(),
        uploadedBy: currentUser?.email || 'desconhecido',
        fileName: confirm.fileName,
        rowCount: confirm.rows.length,
        especialidade: confirm.esp,
        data: confirm.rows,
      });
      setMsg({ type: 'success', text: `✓ ${confirm.rows.length} linhas importadas para ${confirm.esp}!` });
      setConfirm(null);
      loadHistory();
    } catch (e) {
      setMsg({ type: 'error', text: `Erro ao importar: ${e.message}` });
    }
    setUploading(false);
  };

  const handleAddEsp = () => {
    const name = newEspName.trim();
    if (!name) return;
    setExtraEsp(prev => prev.includes(name) ? prev : [...prev, name]);
    setNewEspName('');
    setAddingNew(false);
  };

  return (
    <div style={{ padding: 28, maxWidth: 980 }}>
      <h2 style={{ fontWeight: 800, fontSize: '1.05rem', color: '#1e293b', marginBottom: 6 }}>
        Upload — Visitas Domiciliares
      </h2>
      <p style={{ color: '#64748b', fontSize: '0.82rem', marginBottom: 20 }}>
        Uma planilha por especialidade — clique (ou arraste o arquivo) no card correspondente pra importar.
        Aceita CSV separado por <code>;</code> ou o arquivo bruto <code>TDEXP_VISITASNR.TXT</code> do IW (separado por <code>|</code>), detectado automaticamente. Codificação Latin-1.
      </p>

      {/* Feedback */}
      {msg && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 18, fontSize: '0.85rem', fontWeight: 600,
          background: msg.type === 'success' ? '#f0fdf4' : '#fef2f2',
          color: msg.type === 'success' ? '#16a34a' : '#dc2626',
          border: `1px solid ${msg.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
        }}>
          <div>{msg.text}</div>
          {msg.type === 'success' && (
            <div style={{ marginTop: 8 }}>
              <Link to="/dashboard-visitas" style={{ color: '#0F766E', textDecoration: 'underline', fontWeight: 700 }}>
                → Ir para o Painel de Visitas Domiciliares
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Grid de cards por especialidade */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 12, marginBottom: 30 }}>
        {cards.map(esp => (
          <EspCard key={esp} esp={esp} meta={latestByEsp[esp]} highlighted={esp === espFromLink} onFile={handleFile} />
        ))}

        {/* Card de adicionar nova especialidade */}
        <div style={{
          border: '1.5px dashed #cbd5e1', borderRadius: 12, padding: '14px 16px',
          display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 88,
        }}>
          {addingNew ? (
            <div>
              <input
                autoFocus value={newEspName} onChange={e => setNewEspName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddEsp(); if (e.key === 'Escape') setAddingNew(false); }}
                placeholder="Nome da especialidade"
                style={{ width: '100%', padding: '7px 9px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.8rem', boxSizing: 'border-box', outline: 'none', marginBottom: 6 }}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={handleAddEsp} style={{ flex: 1, padding: '5px 8px', borderRadius: 6, border: 'none', background: C.primary, color: '#fff', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}>Adicionar</button>
                <button onClick={() => { setAddingNew(false); setNewEspName(''); }} style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer' }}>✕</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAddingNew(true)} style={{
              border: 'none', background: 'transparent', color: '#94a3b8', fontWeight: 700, fontSize: '0.8rem',
              cursor: 'pointer', padding: 0, textAlign: 'center',
            }}>
              + Nova especialidade
            </button>
          )}
        </div>
      </div>

      {/* Modal de confirmação antes de gravar */}
      {confirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'grid', placeItems: 'center', zIndex: 100, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 14, padding: 24, width: 420, maxWidth: '100%' }}>
            <div style={{ fontWeight: 800, fontSize: '1rem', color: '#1e293b', marginBottom: 4 }}>Confirmar importação</div>
            <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 14 }}>
              <b style={{ color: '#0F766E' }}>{confirm.esp}</b> · {confirm.fileName} · {confirm.rows.length} linhas
            </div>
            {confirm.detected && confirm.detected !== confirm.esp && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: '0.78rem', padding: '8px 12px', borderRadius: 8, marginBottom: 14 }}>
                ⚠️ O conteúdo do arquivo parece ser de "<b>{confirm.detected}</b>", mas você está importando no card "<b>{confirm.esp}</b>". Confira se é o arquivo certo.
              </div>
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={handleConfirmImport} disabled={uploading}
                style={{ flex: 1, padding: 12, borderRadius: 8, border: 'none', background: C.primary, color: '#fff', fontWeight: 700, cursor: uploading ? 'wait' : 'pointer', fontSize: '0.9rem' }}>
                {uploading ? '⏳ Importando...' : '📤 Confirmar e importar'}
              </button>
              <button onClick={() => setConfirm(null)} disabled={uploading}
                style={{ padding: '12px 18px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Histórico completo */}
      {history.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Histórico de uploads
            </div>
            {especialidadesNoHistorico.length > 1 && (
              <select value={historyFilter} onChange={e => setHistoryFilter(e.target.value)}
                style={{ marginLeft: 'auto', fontSize: '0.75rem', padding: '4px 8px', borderRadius: 6, border: '1px solid #e2e8f0', color: '#475569' }}>
                <option value="">Todas as especialidades</option>
                {especialidadesNoHistorico.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filteredHistory.map((h) => (
              <div key={h.id} style={{
                background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
                padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <span style={{ fontSize: 18 }}>📋</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.83rem', color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.fileName}</div>
                  <div style={{ fontSize: '0.73rem', color: '#94a3b8' }}>
                    {h.especialidade ? <b style={{ color: '#0F766E' }}>{h.especialidade}</b> : '(sem especialidade)'} · {h.rowCount} linhas · {h.uploadedBy} · {h.uploadedAt?.toLocaleString?.('pt-BR') || 'data indisponível'}
                  </div>
                </div>
                {latestPerEspecialidade[h.id] && (
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, background: '#f0f0ff', color: C.primary, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                    Atual
                  </span>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(h.id); }}
                  style={{
                    border: 'none', background: 'transparent', cursor: 'pointer',
                    color: '#ef4444', fontSize: '1rem', padding: '4px 8px',
                    borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  title="Excluir planilha"
                >
                  🗑️
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
